import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useMutation, useManualQuery } from "graphql-hooks";
import {
  Box,
  Typography,
  Avatar,
  Card,
  CardContent,
  CardMedia,
  Tooltip,
  Button,
  Divider,
} from "@mui/material";
import { Helmet } from "react-helmet-async";
import ReactPlayer from "react-player";
import FavoriteIcon from "@mui/icons-material/Favorite";
import FavoriteBorderOutlined from "@mui/icons-material/FavoriteBorderOutlined";
import ModeCommentOutlined from "@mui/icons-material/ModeCommentOutlined";
import ShareOutlined from "@mui/icons-material/ShareOutlined";
import CollectionsBookmarkOutlinedIcon from "@mui/icons-material/CollectionsBookmarkOutlined";
import CollectionsBookmarkIcon from "@mui/icons-material/CollectionsBookmark";
import FlagOutlined from "@mui/icons-material/FlagOutlined";
import FollowButton from "./components/FollowButton";
import FloatingMenu from "./components/FloatingMenu";
import ReportDialog from "./components/ReportDialog";
import CommentDialog from "./components/CommentDialog";
import UserCard from "./components/UserCard";
import SharePopup from "./components/SharePopup";
import AlertPopup from "./components/AlertPopup";
import dayjs from "dayjs";
import { isValidUUIDv4 } from "./hooks/uuid";

import {
  VIDEO_BY_UUID,
  CREATE_VIDEO_LIKE_MUTATION,
  DELETE_VIDEO_LIKE_MUTATION,
  UPDATE_VIDEO_SHARE_NO,
  CREATE_VIDEO_COMMENT_MUTATION,
  DELETE_VIDEO_COMMENT_MUTATION,
  EDIT_VIDEO_COMMENT_MUTATION,
  CREATE_VIDEO_COMMENT_LIKE_MUTATION,
  DELETE_VIDEO_COMMENT_LIKE_MUTATION,
  CREATE_VIDEO_COMMENT_REPLY_MUTATION,
  DELETE_VIDEO_COMMENT_REPLY_MUTATION,
  EDIT_VIDEO_COMMENT_REPLY_MUTATION,
  CREATE_VIDEO_COMMENT_REPLY_LIKE_MUTATION,
  DELETE_VIDEO_COMMENT_REPLY_LIKE_MUTATION,
} from "./graphQL/video_qry";

import {
  CREATE_FOLLOW_MUTATION,
  DELETE_FOLLOW_MUTATION,
} from "./graphQL/follow_qry";

import {
  CREATE_COLLECTION_MUTATION,
  DELETE_COLLECTION_MUTATION,
} from "./graphQL/collection_qry";

// Import Avatar images
import Avatar3 from "./assets/SearchResults/Avatars/Robert_Thompson.png";

import placeholderImage from "./assets/r3play-logo-sml.png";

//Tooltip Component
const StyledTooltip = ({ title, children }) => (
  <Tooltip
    title={title}
    placement="top"
    arrow
    PopperProps={{
      modifiers: [
        {
          name: "offset",
          options: {
            offset: [0, -5],
          },
        },
      ],
    }}
    componentsProps={{
      tooltip: {
        sx: {
          bgcolor: "#666",
          color: "white",
          fontSize: "12px",
          fontWeight: "500",
          borderRadius: "4px",
          padding: "6px 8px",
          "& .MuiTooltip-arrow": {
            color: "#666",
          },
        },
      },
    }}
  >
    {children}
  </Tooltip>
);

export default function VideoDetail() {
  const { videoUuid } = useParams();
  const navigate = useNavigate();
  const [videoData, setVideoData] = useState({});
  const [videoDataObj, setVideoDataObj] = useState({});
  const [isLiked, setIsLiked] = useState(false);
  const [likeCount, setLikeCount] = useState(0);
  const [shareCount, setShareCount] = useState(0);
  const [commentCount, setCommentCount] = useState(0);
  const [isInCollection, setIsInCollection] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isOwnVideo, setIsOwnVideo] = useState(false);
  const [isInvalidURL, setIsInvalidURL] = useState(false);
  const [floatMenuLeftMargin, setFloatMenuLeftMargin] = useState(0);
  const [reportDialog, setReportDialog] = useState({ open: false });
  const [shareDialog, setShareDialog] = useState({ open: false });
  const shareUrl = `${window.location.origin}/video/${videoUuid}`;
  let role = localStorage.getItem("role") ? localStorage.getItem("role") : null;
  const [unfollowPopup, setUnfollowPopup] = useState({
    open: false,
    followingName: "",
  });
  const [alertPopup, setAlertPopup] = useState({
    open: false,
  });
  const [commentDialog, setCommentDialog] = useState({
    open: false,
    product: null,
  });

  //remove from collection by sami
  const [confirmPopup, setConfirmPopup] = useState({
    open: false,
    title: "",
  });

  //add to collection by sami
  const [successPopup, setSuccessPopup] = useState({
    open: false,
    title: "",
  });

  // Helper function to get video thumbnail from any platform
  const getVideoThumbnail = (videoUrl) => {
    if (!videoUrl) return "";

    // YouTube
    if (videoUrl.includes("youtube.com") || videoUrl.includes("youtu.be")) {
      let videoId = "";
      if (videoUrl.includes("youtu.be/")) {
        videoId = videoUrl.split("youtu.be/")[1].split("?")[0];
      } else if (videoUrl.includes("youtube.com")) {
        const urlParams = new URLSearchParams(videoUrl.split("?")[1]);
        videoId = urlParams.get("v");
      }
      return `https://img.youtube.com/vi/${videoId}/maxresdefault.jpg`;
    }

    // Vimeo
    if (videoUrl.includes("vimeo.com")) {
      const videoId = videoUrl.split("/").pop().split("?")[0];
      return `https://vumbnail.com/${videoId}.jpg`;
    }

    // Dailymotion
    if (videoUrl.includes("dailymotion.com") || videoUrl.includes("dai.ly")) {
      let videoId = "";
      if (videoUrl.includes("dai.ly/")) {
        videoId = videoUrl.split("dai.ly/")[1].split("?")[0];
      } else {
        videoId = videoUrl.split("/video/")[1].split("?")[0];
      }
      return `https://www.dailymotion.com/thumbnail/video/${videoId}`;
    }

    // Default fallback - return empty or a placeholder
    return "";
  };

  const [createLike] = useMutation(CREATE_VIDEO_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unLike] = useMutation(DELETE_VIDEO_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [updateVideoShare] = useMutation(UPDATE_VIDEO_SHARE_NO, {
    awaitRefetchQueries: true,
  });

  const [createComment] = useMutation(CREATE_VIDEO_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [editComment] = useMutation(EDIT_VIDEO_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteComment] = useMutation(DELETE_VIDEO_COMMENT_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [createVideoCommentLike] = useMutation(
    CREATE_VIDEO_COMMENT_LIKE_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [unLikeVideoComment] = useMutation(DELETE_VIDEO_COMMENT_LIKE_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [createVideoCommentReply] = useMutation(
    CREATE_VIDEO_COMMENT_REPLY_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [editVideoCommentReply] = useMutation(
    EDIT_VIDEO_COMMENT_REPLY_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [deleteVideoCommentReply] = useMutation(
    DELETE_VIDEO_COMMENT_REPLY_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [createVideoCommentReplyLike] = useMutation(
    CREATE_VIDEO_COMMENT_REPLY_LIKE_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  const [unLikeVideoCommentReply] = useMutation(
    DELETE_VIDEO_COMMENT_REPLY_LIKE_MUTATION,
    {
      awaitRefetchQueries: true,
    }
  );

  let userUuid = localStorage.getItem("userUuid")
    ? localStorage.getItem("userUuid")
    : null;
  const currentUserName = localStorage.getItem("userName") || "";

  const [getVideoData] = useManualQuery(VIDEO_BY_UUID, {
    fetchPolicy: "network-only",
  });

  const [createFollow] = useMutation(CREATE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [unFollow] = useMutation(DELETE_FOLLOW_MUTATION, {
    awaitRefetchQueries: true,
  });

  //Sami
  const [createCollection] = useMutation(CREATE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const [deleteCollection] = useMutation(DELETE_COLLECTION_MUTATION, {
    awaitRefetchQueries: true,
  });

  const encodeText = (text) => {
    return encodeURIComponent(text);
  };

  const loadVideoInfo = async () => {
    if (!videoUuid) {
      setIsInvalidURL(true);
      return;
    }
    if (isValidUUIDv4(videoUuid)) {
      const {
        loading: loadingVideoData,
        error: errorVideoData,
        data: videoData,
      } = await getVideoData({
        variables: {
          uuid: videoUuid,
        },
      });
      if (!loadingVideoData && videoData && videoData.videoByUuid) {
        {
          /* check if video is not published and login-user is not owner display error message */
        }
        if (
          role === "Admin" ||
          videoData.videoByUuid.isPublished === true ||
          videoData.videoByUuid.userId.userUuid === userUuid
        ) {
          console.log("data:", videoData.videoByUuid);
          setVideoDataObj(videoData.videoByUuid);
        } else {
          setAlertPopup({
            open: true,
            title: "This page is not available at this time.",
          });
        }
      } else setIsInvalidURL(true);
      if (errorVideoData) {
        console.log("errorCurrVideoData: ", errorVideoData);
        setIsInvalidURL(true);
      }
    } else setIsInvalidURL(true);
  };

  const setVideoInfo = async () => {
    if (!videoDataObj || !videoDataObj.userId) return;
    setVideoData({
      id: videoDataObj.uuid,
      videoId: videoDataObj.videoId,
      title: videoDataObj.title,
      video: videoDataObj.content,
      videoUrl: videoDataObj.videoUrl,
      img: getVideoThumbnail(videoDataObj.videoUrl) || placeholderImage,
      duration: videoDataObj.duration,
      userId: {
        userId: videoDataObj.userId.id,
        userUuid: videoDataObj.userId.userUuid,
        firstName: videoDataObj.userId.firstName,
        lastName: videoDataObj.userId.lastName,
        username: videoDataObj.userId.username,
        avatarFileName: videoDataObj.userId.avatarFileName,
        isFollowing: videoDataObj.userId.isLoggedInUserFollowing,
        listings: videoDataObj.userId.listings,
        soldToys: videoDataObj.userId.soldToys,
        ratingNo: videoDataObj.userId.ratingNo,
        ratingCount: videoDataObj.userId.ratingCount,
        dateJoined: videoDataObj.userId.dateJoined,
      },
      date: videoDataObj.publishedDate
        ? dayjs(videoDataObj.publishedDate).format("MMM D, YYYY")
        : "",
      likes: videoDataObj.likeNo,
      shares: videoDataObj.shareNo,
      isFavorited: false,
      isInCollection: videoDataObj.isInCollection,
      comments: videoDataObj.videoComments,
    });
    setCommentCount(videoDataObj.commentNo);
    setIsFollowing(videoDataObj.userId.isLoggedInUserFollowing);
    setIsLiked(videoDataObj.isLiked);
    setLikeCount(videoDataObj.likeNo);
    setShareCount(videoDataObj.shareNo);
    setIsInCollection(videoDataObj.isInCollection);

    // Check if current user is the video author
    setIsOwnVideo(videoDataObj.userId.userUuid === userUuid);
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    (async () => loadVideoInfo())();
  }, [videoUuid]);

  useEffect(() => {
    (async () => setVideoInfo())();
  }, [videoDataObj]);

  // Set floating menu place based on screen size
  useEffect(() => {
    const handleResize = () => {
      setFloatMenuLeftMargin(window.innerWidth - 100);
    };
    handleResize();
    window.addEventListener("resize", handleResize);
  }, []);

  const handleLikeToggle = async () => {
    //save video like/unlike in database
    if (!isLiked) {
      const { data, error } = await createLike({
        variables: {
          videoUuid,
        },
      });

      if (error) {
        console.log("Error in create video like", error, data);
      } else {
        console.log("Success in create video like", data);
        setIsLiked(!isLiked);
        setLikeCount((prevCount) => (isLiked ? prevCount - 1 : prevCount + 1));
      }
    } else {
      const { data, error } = await unLike({
        variables: {
          videoUuid,
        },
      });

      if (error) {
        console.log("Error in delete video like", error, data);
      } else {
        console.log("Success in delete video like", data);
        setIsLiked(!isLiked);
        setLikeCount((prevCount) => (isLiked ? prevCount - 1 : prevCount + 1));
      }
    }
  };

  const handleBookmarkClick = () => {
    if (isInCollection) {
      // Show removal confirmation
      setConfirmPopup({
        open: true,
        title: videoData.title,
      });
    } else {
      // Show add alert
      setSuccessPopup({
        open: true,
        title: videoData.title,
      });
    }
  };

  const handleConfirmAdd = async () => {
    await createCollection({
      variables: {
        objectId: parseInt(videoData.videoId),
        contentType: "video",
      },
    });

    setIsInCollection(true);
    setSuccessPopup({ open: false, title: "" });
  };

  const handleConfirmRemove = async () => {
    await deleteCollection({
      variables: {
        objectId: parseInt(videoData.videoId),
        contentType: "video",
      },
    });

    setIsInCollection(false);
    setConfirmPopup({ open: false, title: "" });
  };
  // Handle cancel action
  const handleCancelAction = () => {
    setConfirmPopup({ open: false, title: "" });
    setSuccessPopup({ open: false, title: "" });
  };

  const handleFollowToggle = async () => {
    if (isFollowing) {
      // Show unfollow confirmation popup
      setUnfollowPopup({
        open: true,
        followingName:
          videoData.userId.firstName + " " + videoData.userId.lastName,
      });
    } else {
      //save follow to database
      const { data, error } = await createFollow({
        variables: {
          userUuid: videoData.userId.userUuid,
        },
      });

      if (error) {
        console.log("Error", error, data);
      } else {
        console.log("Success", data);
        // Direct follow action
        setIsFollowing(!isFollowing);
        setVideoData((prev) => ({
          ...prev,
          userId: { ...prev.userId, isFollowing: !isFollowing },
        }));
      }
    }
  };

  const handleConfirmUnfollow = async () => {
    //save unfollow to database
    const { data, error } = await unFollow({
      variables: {
        userUuid: videoData.userId.userUuid,
      },
    });

    if (error) {
      console.log("Error", error, data);
    } else {
      console.log("Success", data);
      setIsFollowing(!isFollowing);
      setUnfollowPopup({ open: false, followingName: "" });
    }
  };

  const handleCancelUnfollow = () => {
    setUnfollowPopup({ open: false, followingName: "" });
  };

  const handleCommentLikeUpdate = async (commentUuid, isLiked) => {
    //save video comment like/unlike in database
    if (isLiked) {
      const { data, error } = await createVideoCommentLike({
        variables: {
          videoCommentUuid: commentUuid,
        },
      });

      if (error) {
        console.log("Error in create video comment like", error, data);
      } else {
        console.log("Success in create video comment like", data);
      }
    } else {
      const { data, error } = await unLikeVideoComment({
        variables: {
          videoCommentUuid: commentUuid,
        },
      });

      if (error) {
        console.log("Error in delete video comment like", error, data);
      } else {
        console.log("Success in delete video comment like", data);
      }
    }

    setVideoData((prev) => ({
      ...prev,
      comments: prev.comments.map((comment) =>
        comment.uuid === commentUuid
          ? {
              ...comment,
              isLiked,
              likeNo: isLiked
                ? comment.likeNo + 1
                : comment.likeNo > 0
                ? comment.likeNo - 1
                : 0,
            }
          : comment
      ),
    }));
  };

  const handleCommentReplyLikeUpdate = async (
    commentUuid,
    commentReplyUuid,
    isLiked
  ) => {
    //save video comment reply like/unlike in database
    if (isLiked) {
      const { data, error } = await createVideoCommentReplyLike({
        variables: {
          videoCommentReplyUuid: commentReplyUuid,
        },
      });

      if (error) {
        console.log("Error in create video comment reply like", error, data);
        return;
      } else {
        console.log("Success in create video comment reply like", data);
      }
    } else {
      const { data, error } = await unLikeVideoCommentReply({
        variables: {
          videoCommentReplyUuid: commentReplyUuid,
        },
      });

      if (error) {
        console.log("Error in delete video comment reply like", error, data);
        return;
      } else {
        console.log("Success in delete video comment reply like", data);
      }
    }
    setVideoData((prev) => ({
      ...prev,
      comments:
        prev.comments &&
        prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies:
                  comment.replies &&
                  comment.replies.map((commentReply) =>
                    commentReply.uuid === commentReplyUuid
                      ? {
                          ...commentReply,
                          isLiked,
                          likeNo: isLiked
                            ? commentReply.likeNo + 1
                            : commentReply.likeNo > 0
                            ? commentReply.likeNo - 1
                            : 0,
                        }
                      : commentReply
                  ),
              }
            : comment
        ),
    }));
  };

  const handleCommentClick = () => {
    setCommentDialog({
      open: true,
      product: videoData,
    });
  };

  const handleClose = () => {
    setAlertPopup({ open: false, title: "" });
  };

  // Handle cancel removal
  const handleCloseCommentDlg = () => {
    setCommentDialog({ open: false, product: null });
  };

  const handleAddComment = async (comntText) => {
    if (!comntText.trim()) return;
    let commentText = encodeText(comntText);
    //Save video comment in database
    const { data, error } = await createComment({
      variables: {
        videoUuid: videoUuid,
        content: commentText,
      },
    });

    if (error) {
      console.log("Error in creating video comment", error, data);
    } else {
      console.log("Success in creating video comment", data);
      setVideoData((prev) => ({
        ...prev,
        comments: [
          ...(prev.comments || []),
          data.createVideoComment.videoComment,
        ],
      }));
      setCommentCount(commentCount + 1);
    }
  };

  const handleAddReply = async (repText, commentUuid) => {
    if (!repText.trim() || !commentUuid) return;
    let replyText = encodeText(repText);

    //Save video comment reply in database
    const { data, error } = await createVideoCommentReply({
      variables: {
        videoCommentUuid: commentUuid,
        content: replyText,
      },
    });

    if (error) {
      console.log("Error in creating video comment reply", error, data);
    } else {
      console.log("Success in creating video comment reply", data);
      setVideoData((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies: [
                  ...(comment.replies || []),
                  data.createVideoCommentReply.videoCommentReply,
                ],
              }
            : comment
        ),
      }));
    }
  };

  const handleDeleteComment = async (commentUuid) => {
    if (!commentUuid) return;
    const { data, error } = await deleteComment({
      variables: {
        videoCommentUuid: commentUuid,
      },
    });

    if (error) {
      console.log("Error in delete video comment", error, data);
    } else {
      console.log("Success in delete video comment", data);
      setVideoData((prev) => ({
        ...prev,
        comments: prev.comments.filter((c) => c.uuid !== commentUuid),
      }));
      setCommentCount(commentCount - 1);
    }
  };

  const handleDeleteReply = async (commentUuid, replyUuid) => {
    if (!commentUuid || !replyUuid) return;
    const { data, error } = await deleteVideoCommentReply({
      variables: {
        videoCommentReplyUuid: replyUuid,
      },
    });

    if (error) {
      console.log("Error in delete video comment reply", error, data);
    } else {
      console.log("Success in delete video comment reply", data);
      setVideoData((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies: comment.replies.filter(
                  (reply) => reply.uuid !== replyUuid
                ),
              }
            : comment
        ),
      }));
    }
  };

  const handleEditComment = async (commentUuid, newEditText) => {
    if (!commentUuid || !newEditText.trim()) return;
    let newText = encodeText(newEditText);
    //Edit video comment in database
    const { data, error } = await editComment({
      variables: {
        videoCommentUuid: commentUuid,
        content: newText,
      },
    });

    if (error) {
      console.log("Error in editing video comment", error, data);
    } else {
      console.log("Success in editing video comment", data);
      setVideoData((prev) => ({
        ...prev,
        comments: prev.comments.map((c) =>
          c.uuid === commentUuid ? { ...c, content: newText } : c
        ),
      }));
    }
  };

  const handleEditReply = async (commentUuid, replyUuid, newEditText) => {
    if (!commentUuid || !replyUuid || !newEditText.trim()) return;
    let newText = encodeText(newEditText);
    //Edit video comment reply in database
    const { data, error } = await editVideoCommentReply({
      variables: {
        videoCommentReplyUuid: replyUuid,
        content: newText,
      },
    });

    if (error) {
      console.log("Error in editing video comment reply", error, data);
    } else {
      console.log("Success in editing video comment reply", data);
      setVideoData((prev) => ({
        ...prev,
        comments: prev.comments.map((comment) =>
          comment.uuid === commentUuid
            ? {
                ...comment,
                replies: comment.replies.map((reply) =>
                  reply.uuid === replyUuid
                    ? { ...reply, content: newText }
                    : reply
                ),
              }
            : comment
        ),
      }));
    }
  };

  // Handle Report Dialog Open Action
  const handleReportDialogOpen = () => {
    setReportDialog({
      open: true,
    });
  };

  // Handle Report Dialog Close action
  const handleReportDialogClose = () => {
    setReportDialog({ open: false });
  };

  // Handle Share Dialog Open Action
  const handleShareDialogOpen = () => {
    setShareDialog({ open: true });
  };

  const handleToyShareSuccess = async () => {
    if (!videoUuid) return;
    const { data, error } = await updateVideoShare({
      variables: {
        videoUuid,
      },
    });

    if (error) {
      console.log("Error in updating video share", error, data);
    } else {
      console.log("Success in updating video share", data);
      setShareCount((prevCount) => prevCount + 1);
    }
  };

  // Handle Share Dialog Close action
  const handleShareDialogClose = () => {
    setShareDialog({ open: false });
  };

  const handleUserProfileNavigation = (user) => {
    // Navigate to user's public homepage
    navigate(`/user/${user.userUuid}/profile`);
  };

  return (
    <>
      {Object.keys(videoData).length > 0 ? (
        <Box sx={{ backgroundColor: "#f5f5f5" }}>
          <Box
            sx={{
              maxWidth: "1440px",
              minHeight: "100vh",
              py: 5,
              px: "100px",
              "@media (max-width: 1360px)": {
                px: "16px",
              },
            }}
          >
            <Helmet>
              <title>R3PLAY - {videoData.title}</title>
              <meta
                name="description"
                content={videoData.video.substring(0, 150)}
              />
            </Helmet>

            {/* Page Header */}
            <Box sx={{ textAlign: "center", mb: 4 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: "30px",
                  "@media (max-width: 856px)": {
                    fontSize: "24px",
                  },
                  fontWeight: "700",
                  color: "#333",
                  mb: 1,
                }}
              >
                Video
              </Typography>
              <Typography
                sx={{
                  fontSize: "16px",
                  color: "#999",
                  "@media (max-width: 856px)": {
                    fontSize: "14px",
                  },
                }}
              >
                Every toy has a story — share yours with the world.
              </Typography>
            </Box>

            {/* Video Card */}
            <Card
              sx={{
                width: 768,
                margin: "0 auto",
                borderRadius: "12px",
                display: "flex",
                flexDirection: "column",
                boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                "@media (max-width: 1024px)": {
                  width: 650,
                },
                "@media (max-width: 856px)": {
                  width: 361,
                },
              }}
            >
              <Box>
                {/* User Header */}
                <Box
                  sx={{
                    p: "50px",
                    pb: 3,
                    "@media (max-width: 856px)": {
                      p: 2,
                    },
                  }}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      backgroundColor: "#F5F5F5",
                      borderRadius: "75px",
                      p: 2,
                      height: 72,
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                      <UserCard
                        user={videoData.userId}
                        onUsernameClick={handleUserProfileNavigation}
                        onFullNameClick={handleUserProfileNavigation}
                        type="avatar"
                        context="detail"
                      >
                        <Avatar
                          src={videoData.userId.avatarFileName}
                          sx={{ width: 48, height: 48, cursor: "pointer" }}
                        />
                      </UserCard>
                      <Box>
                        <UserCard
                          user={videoData.userId}
                          onUsernameClick={handleUserProfileNavigation}
                          onFullNameClick={handleUserProfileNavigation}
                          type="username"
                          context="detail"
                        >
                          <Typography
                            variant="body2"
                            fontWeight="medium"
                            sx={{ cursor: "pointer" }}
                          >
                            {videoData.userId.username}
                          </Typography>
                        </UserCard>
                      </Box>
                    </Box>
                    {!isOwnVideo && (
                      <FollowButton
                        isFollowing={isFollowing}
                        onToggle={handleFollowToggle}
                      />
                    )}
                  </Box>
                </Box>

                {/* Video Title */}
                <Box
                  sx={{
                    px: "50px",
                    pb: 3,
                    "@media (max-width: 856px)": {
                      px: 2,
                    },
                  }}
                >
                  <Typography
                    variant="h4"
                    sx={{
                      fontSize: "28px",
                      fontWeight: "700",
                      color: "#333",
                      mb: 1,
                      "@media (max-width: 856px)": {
                        fontSize: "20px",
                      },
                    }}
                  >
                    {videoData.title}
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: "14px",
                      color: "#999",
                    }}
                  >
                    {videoData.date}
                  </Typography>
                </Box>

                {/* Video Player */}
                <Box
                  sx={{
                    px: "50px",
                    pb: 3,
                    "@media (max-width: 856px)": {
                      px: 2,
                    },
                  }}
                >
                  <CardMedia
                    sx={{
                      width: 668,
                      height: 668, //376, // 16:9 ratio of 668
                      position: "relative",
                      backgroundColor: "#000",
                      borderRadius: "8px",
                      overflow: "hidden",
                      "@media (max-width: 1024px)": {
                        width: 550,
                        height: 550, //309, // 16:9 ratio of 550
                      },
                      "@media (max-width: 856px)": {
                        width: 329,
                        height: 329, //185, // 16:9 ratio of 329
                      },
                    }}
                  >
                    <ReactPlayer
                      url={videoData.videoUrl}
                      width="100%"
                      height="100%"
                      controls
                      style={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                      }}
                    />
                  </CardMedia>
                </Box>

                {/* Video Description */}
                <CardContent
                  sx={{
                    px: "50px",
                    pb: 3,
                    "@media (max-width: 856px)": {
                      px: 2,
                    },
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: "16px",
                      color: "#666",
                      lineHeight: 1.8,
                      wordBreak: "break-word",
                      overflowWrap: "break-word",
                      "@media (max-width: 856px)": {
                        fontSize: "14px",
                      },
                    }}
                  >
                    {videoData.video}
                  </Typography>
                </CardContent>
              </Box>

              {/* Divider */}
              <Divider
                sx={{
                  mx: "50px",
                  "@media (max-width: 856px)": {
                    mx: 2,
                  },
                }}
              />

              {/* Action Buttons */}
              <Box
                sx={{
                  px: "50px",
                  py: 3,
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  flexWrap: "nowrap",
                  gap: 1,
                  "@media (max-width: 856px)": {
                    px: 2,
                    gap: 0.5,
                  },
                }}
              >
                {/* Left side - Like, Comment, Share, Collection buttons */}
                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    flexWrap: "nowrap",
                  }}
                >
                  <StyledTooltip
                    title={isLiked ? "Click to undo like" : "Click to like"}
                  >
                    <Button
                      onClick={handleLikeToggle}
                      startIcon={
                        isLiked ? (
                          <FavoriteIcon
                            sx={{ fontSize: "18px", color: "#EF5658" }}
                          />
                        ) : (
                          <FavoriteBorderOutlined
                            sx={{ fontSize: "18px", color: "#999" }}
                          />
                        )
                      }
                      sx={{
                        border: "1px solid #ddd",
                        color: "#999",
                        fontSize: "13px",
                        textTransform: "none",
                        height: "32px",
                        px: 1.5,
                        borderRadius: "4px",
                        minWidth: "auto",
                        "@media (max-width: 856px)": {
                          px: 0.5,
                          "& .MuiButton-startIcon": {
                            margin: 0,
                          },
                        },
                        "&:hover": {
                          borderColor: "#EF5658",
                          color: "#EF5658",
                          "& .MuiSvgIcon-root": {
                            color: "#EF5658",
                          },
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          "@media (max-width: 856px)": {
                            display: "none",
                          },
                        }}
                      >
                        Like
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        {likeCount}
                      </Box>
                    </Button>
                  </StyledTooltip>

                  <StyledTooltip title="View/Add comments">
                    <Box onClick={handleCommentClick}>
                      <Button
                        startIcon={
                          <ModeCommentOutlined
                            sx={{ fontSize: "18px", color: "#999" }}
                          />
                        }
                        sx={{
                          border: "1px solid #ddd",
                          color: "#999",
                          fontSize: "13px",
                          textTransform: "none",
                          height: "32px",
                          px: 1.5,
                          borderRadius: "4px",
                          minWidth: "auto",
                          "@media (max-width: 856px)": {
                            px: 0.5,
                            "& .MuiButton-startIcon": {
                              margin: 0,
                            },
                          },
                          "&:hover": {
                            borderColor: "#EF5658",
                            color: "#EF5658",
                            "& .MuiSvgIcon-root": {
                              color: "#EF5658",
                            },
                          },
                        }}
                      >
                        <Box
                          component="span"
                          sx={{
                            "@media (max-width: 856px)": {
                              display: "none",
                            },
                          }}
                        >
                          Comment
                        </Box>
                        <Box
                          component="span"
                          sx={{
                            ml: 0.5,
                            fontSize: "13px",
                            fontWeight: "500",
                          }}
                        >
                          {commentCount}
                        </Box>
                      </Button>
                    </Box>
                  </StyledTooltip>

                  <StyledTooltip title="Share this video">
                    <Button
                      onClick={handleShareDialogOpen}
                      startIcon={
                        <ShareOutlined
                          sx={{ fontSize: "18px", color: "#999" }}
                        />
                      }
                      sx={{
                        border: "1px solid #ddd",
                        color: "#999",
                        fontSize: "13px",
                        textTransform: "none",
                        height: "32px",
                        px: 1.5,
                        borderRadius: "4px",
                        minWidth: "auto",
                        "@media (max-width: 856px)": {
                          px: 0.5,
                          "& .MuiButton-startIcon": {
                            margin: 0,
                          },
                        },
                        "&:hover": {
                          borderColor: "#EF5658",
                          color: "#EF5658",
                          "& .MuiSvgIcon-root": {
                            color: "#EF5658",
                          },
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          "@media (max-width: 856px)": {
                            display: "none",
                          },
                        }}
                      >
                        Share
                      </Box>
                      <Box
                        component="span"
                        sx={{
                          ml: 0.5,
                          fontSize: "13px",
                          fontWeight: "500",
                        }}
                      >
                        {shareCount ? shareCount : 0}
                      </Box>
                    </Button>
                  </StyledTooltip>

                  <StyledTooltip
                    title={
                      isInCollection
                        ? "Remove from your collection"
                        : "Add to your collection"
                    }
                  >
                    <Button
                      onClick={handleBookmarkClick}
                      startIcon={
                        isInCollection ? (
                          <CollectionsBookmarkIcon
                            sx={{ fontSize: "18px", color: "#000" }}
                          />
                        ) : (
                          <CollectionsBookmarkOutlinedIcon
                            sx={{ fontSize: "18px", color: "#999" }}
                          />
                        )
                      }
                      sx={{
                        border: "1px solid #ddd",
                        color: "#999",
                        fontSize: "13px",
                        textTransform: "none",
                        height: "32px",
                        px: 1.5,
                        borderRadius: "4px",
                        minWidth: "auto",
                        "@media (max-width: 856px)": {
                          px: 0.5,
                          "& .MuiButton-startIcon": {
                            margin: 0,
                          },
                        },
                        "&:hover": {
                          borderColor: "#EF5658",
                          color: "#EF5658",
                          "& .MuiSvgIcon-root": {
                            color: "#EF5658",
                          },
                        },
                      }}
                    >
                      <Box
                        component="span"
                        sx={{
                          "@media (max-width: 856px)": {
                            display: "none",
                          },
                        }}
                      >
                        Collection
                      </Box>
                    </Button>
                  </StyledTooltip>
                </Box>

                {/* Right side - Report button */}
                <Box>
                  <StyledTooltip title="Report this video">
                    <Button
                      onClick={handleReportDialogOpen}
                      startIcon={
                        <FlagOutlined
                          sx={{ fontSize: "18px", color: "#999" }}
                        />
                      }
                      sx={{
                        color: "#999",
                        fontSize: "13px",
                        textTransform: "none",
                        height: "32px",
                        borderRadius: "4px",
                        minWidth: "auto",
                        "@media (max-width: 856px)": {
                          "& .MuiButton-startIcon": {
                            margin: 0,
                          },
                        },
                        "&:hover": {
                          backgroundColor: "rgba(239, 86, 88, 0.04)",
                          color: "#EF5658",
                          "& .MuiSvgIcon-root": {
                            color: "#EF5658",
                          },
                        },
                      }}
                    >
                      <Box component="span">Report</Box>
                    </Button>
                  </StyledTooltip>
                </Box>
              </Box>
            </Card>
          </Box>
          <CommentDialog
            open={commentDialog.open}
            product={videoData}
            comments={videoData && videoData.comments ? videoData.comments : []}
            onAddComment={(text) => handleAddComment(text)}
            onAddReply={(reply, commentUuid) =>
              handleAddReply(reply, commentUuid)
            }
            onFollowChange={() => handleFollowToggle}
            onCommentLikeToggle={(newLikeState, commentUuid) =>
              handleCommentLikeUpdate(commentUuid, newLikeState)
            }
            onCommentReplyLikeToggle={(
              newLikeState,
              commentUuid,
              commentReplyUuid
            ) =>
              handleCommentReplyLikeUpdate(
                commentUuid,
                commentReplyUuid,
                newLikeState
              )
            }
            onDeleteComment={(commentUuid) => handleDeleteComment(commentUuid)}
            onDeleteReply={(commentUuid, replyUuid) =>
              handleDeleteReply(commentUuid, replyUuid)
            }
            onEditComment={(commentUuid, newText) =>
              handleEditComment(commentUuid, newText)
            }
            onEditReply={(commentUuid, replyUuid, newText) =>
              handleEditReply(commentUuid, replyUuid, newText)
            }
            onClose={handleCloseCommentDlg}
          />

          {/* Alert Popup for add collection actions */}
          <AlertPopup
            open={successPopup.open}
            title={`Item "${successPopup.title}" is added to your collection.`}
            cancelText=""
            confirmText="OK"
            onConfirm={handleConfirmAdd}
            onClose={() => setSuccessPopup({ open: false, title: "" })}
          />

          {/* Alert Popup for remove collection actions */}
          <AlertPopup
            open={confirmPopup.open}
            title={`Are you sure you want to remove "${confirmPopup.title}" from your collection?`}
            confirmText="Yes, Remove"
            cancelText="Cancel"
            onConfirm={handleConfirmRemove}
            onCancel={() => setConfirmPopup({ open: false, title: "" })}
            onClose={() => setConfirmPopup({ open: false, title: "" })}
          />

          {/* Unfollow Confirmation Popup */}
          <AlertPopup
            open={unfollowPopup.open}
            title="Unfollow This Person"
            message={
              <>
                Are you sure you want to unfollow user{" "}
                <strong>{unfollowPopup.followingName}</strong>?
              </>
            }
            confirmText="YES, UNFOLLOW"
            cancelText="CANCEL"
            onConfirm={handleConfirmUnfollow}
            onCancel={handleCancelUnfollow}
            onClose={handleCancelUnfollow}
            showCheckCircle={true}
          />

          {/* Report Popup */}
          <ReportDialog
            open={reportDialog.open}
            onClose={handleReportDialogClose}
            contentType="video"
            objectId={videoDataObj.videoId}
            objectUuid={videoUuid}
          />

          {/* Share Popup */}
          <SharePopup
            open={shareDialog.open}
            onClose={handleShareDialogClose}
            shareUrl={shareUrl}
            contentTitle={videoDataObj && videoDataObj.title}
            onEmailShareSuccess={handleToyShareSuccess}
          />

          <AlertPopup
            open={alertPopup.open}
            title={alertPopup.title}
            message=""
            confirmText="OK"
            cancelText=""
            onConfirm={handleClose}
            onClose={handleClose}
          />

          <FloatingMenu left={floatMenuLeftMargin} />
        </Box>
      ) : (
        isInvalidURL && (
          <>
            <Helmet>
              <title>R3PLAY - Invalid</title>
            </Helmet>

            {/* Page Header */}
            <Box sx={{ textAlign: "center", mb: 4, mt: 10 }}>
              <Typography
                variant="h3"
                sx={{
                  fontSize: "30px",
                  "@media (max-width: 856px)": {
                    fontSize: "24px",
                  },
                  fontWeight: "700",
                  color: "#333",
                  mb: 1,
                }}
              >
                This URL is not valid.
              </Typography>
              <Button
                variant="contained"
                onClick={() => navigate("/dashboard")}
                sx={{
                  height: "42px",
                  px: 3,
                  mt: 5,
                  fontWeight: 500,
                  backgroundColor: "#EF5658",
                  "@media (max-width: 856px)": {
                    fontSize: "13px",
                    height: "30px",
                    px: 1,
                  },
                }}
              >
                OK
              </Button>
            </Box>
          </>
        )
      )}
    </>
  );
}
